import { normalizeProjectCatalogBrandModel } from "./projectCatalogEnrichment";

export const SANDBOX_CATALOGUE_SOURCE_TYPES = ["manual", "project_memory", "sales_catalogue"] as const;

export type SandboxCatalogueSourceType = (typeof SANDBOX_CATALOGUE_SOURCE_TYPES)[number];

export const SANDBOX_CATALOGUE_SOURCE_LABELS: Record<SandboxCatalogueSourceType, string> = {
  manual: "Manual",
  project_memory: "Project Memory",
  sales_catalogue: "Sales Catalogue",
};

export type SandboxDimensionUnit = "mm" | "cm" | "inch";

export const SANDBOX_PLACEMENT_TYPES = [
  "speaker",
  "subwoofer",
  "projector",
  "screen",
  "camera",
  "keypad",
  "rack",
  "network",
  "lighting",
  "other",
] as const;

export type SandboxPlacementType = (typeof SANDBOX_PLACEMENT_TYPES)[number];

export type SandboxProjectCataloguePlanningMetadata = {
  placementType?: SandboxPlacementType;
  rackUnits?: number;
  [key: string]: unknown;
};

export type AiFoundDimensions = {
  width: number | null;
  height: number | null;
  depth: number | null;
  dimensionUnit: SandboxDimensionUnit;
  sourceUrl: string | null;
  confidence: "high" | "medium" | "low";
  notes: string | null;
};

export type AiEnrichedProduct = {
  width: number | null;
  height: number | null;
  depth: number | null;
  dimensionUnit: SandboxDimensionUnit;
  weight: number | null;
  weightUnit: string | null;
  rackUnits: number | null;
  powerConsumption: string | null;
  voltage: string | null;
  datasheetUrl: string | null;
  placementType: SandboxPlacementType | null;
  sourceUrls: string[];
  confidence: "high" | "medium" | "low";
  notes: string | null;
};

export function parseDecimal(value: string | number | null | undefined): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatSandboxDimensions(
  width: number | string | null | undefined,
  height: number | string | null | undefined,
  depth: number | string | null | undefined,
  unit: SandboxDimensionUnit = "mm",
): string | null {
  const w = parseDecimal(width);
  const h = parseDecimal(height);
  const d = parseDecimal(depth);
  if (w == null && h == null && d == null) return null;

  const parts = [w, h, d].map((v) => (v == null ? "—" : String(Math.round(v * 100) / 100)));
  return `${parts.join(" × ")} ${unit}`;
}

export function formatSandboxWeight(
  weight: number | string | null | undefined,
  weightUnit: string | null | undefined = "kg",
): string | null {
  const w = parseDecimal(weight);
  if (w == null) return null;
  const unit = weightUnit?.trim() || "kg";
  return `${Math.round(w * 100) / 100} ${unit}`;
}

export function sandboxCatalogueNormPair(manufacturer: string, productName: string) {
  return {
    manufacturerNorm: normalizeProjectCatalogBrandModel(manufacturer),
    productNameNorm: normalizeProjectCatalogBrandModel(productName),
  };
}

export function sandboxCatalogueDisplayName(manufacturer: string, productName: string): string {
  const m = manufacturer.trim();
  const p = productName.trim();
  if (!m) return p;
  if (!p) return m;
  if (p.toLowerCase().startsWith(m.toLowerCase())) return p;
  return `${m} ${p}`;
}

/** Sentinel manufacturer filter for empty / review-queue rows. */
export const SANDBOX_NEEDS_REVIEW_BRAND = "Other / Needs Review";

export const SANDBOX_OPTIONAL_CATEGORIES = [
  "Automation",
  "Audio",
  "Video",
  "Lighting",
  "Networking",
  "Security",
  "Accessories",
  "Cables",
  "Other",
] as const;

export type SandboxOptionalCategory = (typeof SANDBOX_OPTIONAL_CATEGORIES)[number];

export type SandboxCatalogueSortMode = "most_used" | "recently_added" | "alphabetical";


export function aiDimensionsFromEnrichment(enrichment: AiEnrichedProduct): AiFoundDimensions {
  return {
    width: enrichment.width,
    height: enrichment.height,
    depth: enrichment.depth,
    dimensionUnit: enrichment.dimensionUnit,
    sourceUrl: enrichment.datasheetUrl ?? enrichment.sourceUrls[0] ?? null,
    confidence: enrichment.confidence,
    notes: enrichment.notes,
  };
}
